import { useEffect } from "react";

/**
 * SEOHead — inject page-specific meta tags into <head>.
 * Usage: <SEOHead title="..." description="..." canonical="..." schema={...} />
 */
export default function SEOHead({
  title,
  description,
  canonical,
  schema,
  robots = "index, follow",
  ogImage = "https://highestefficiencysolarcell.com/og-image.jpg",
  ogType = "website",
}) {
  useEffect(() => {
    // Title
    if (title) document.title = title;

    const setMeta = (name, content, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    if (description) {
      setMeta("description", description);
      setMeta("og:description", description, true);
      setMeta("twitter:description", description);
    }

    if (title) {
      setMeta("og:title", title, true);
      setMeta("twitter:title", title);
    }

    setMeta("robots", robots);
    setMeta("og:type", ogType, true);
    setMeta("og:image", ogImage, true);
    setMeta("twitter:image", ogImage);
    setMeta("twitter:card", "summary_large_image");

    if (canonical) {
      setMeta("og:url", canonical, true);
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    // JSON-LD schema
    if (schema) {
      const existing = document.getElementById("jsonld-schema");
      if (existing) existing.remove();
      const script = document.createElement("script");
      script.id = "jsonld-schema";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      // Cleanup schema on unmount to avoid stale data
      const existing = document.getElementById("jsonld-schema");
      if (existing) existing.remove();
    };
  }, [title, description, canonical, schema, robots, ogImage, ogType]);

  return null;
}